import { useState, useEffect } from 'react';
import styled from 'styled-components';
import { AdditionalCostDetailsDto } from 'types';
import { Checkbox } from 'antd';
import {
    AppButton,
    AppCardListItem,
    AppFlex,
    AppTitle,
    AppTooltipQuestionMarkIcon,
    DetailsColumn,
    DetailsRow,
} from '../../../../../../atoms';
import { commonTexts, settlementTexts } from '../../../../../../../consts/text';
import { AddEditAdditionalCostModal } from '../../../../AddEditAdditionalCostModal/AddEditAdditionalCostModal';
import { settlementVatRateTypeLabels } from '../../../../../../../consts/settlementVatRates/settlementVatRateLabels';
import { settlementVatReleaseReasonLabels } from '../../../../../../../consts/settlementVatRates/settlementVatReleaseReasonLabels';
import { useAppMutation } from '../../../../../../../services/reactQuery/useAppMutation';
import StaffHTTPService from '../../../../../../../services/HTTPService/staff/StaffHTTPService';
import { useSimpleNotification } from '../../../../../../../hooks/useSimpleNotification/useSimpleNotification';
import { RemoveModal } from '../../../../../../organisms';
import { colors } from '../../../../../../../theme/styledComponents/colors';
import { AdditionalCostInvoice } from './AdditionalCostInvoice';

export const Header = styled('div')`
    display: flex;
    justify-content: flex-start;
    align-items: center;
`;

type Props = {
    detailsDto: AdditionalCostDetailsDto;
    index: number;
};

export const AdditionalCostDetails = ({ detailsDto, index }: Props) => {
    const { displaySuccess, displayError } = useSimpleNotification();

    const [manualBillEditModalOpen, setManualBillEditModalOpen] = useState(false);
    const [additionalCostDeleteModalOpen, setAdditionalCostDeleteModalOpen] = useState(false);

    const { mutate: deleteAdditionalCost, isLoading: deleteAdditionalCostIsLoading } =
        useAppMutation(StaffHTTPService.settlements.deleteAdditionalCost, {
            key: ['DELETE_MANUAL_BILL'],
            onSuccess: () => {
                displaySuccess(settlementTexts.children.successfullyDeleteAdditionalCostMessage);
            },
            onError: () => {
                displayError(settlementTexts.children.errorDeleteAdditionalCostMessage);
            },
            invalidateQueryKeys: [
                ['ADDITIONAL_COST_DETAILS'],
                ['ADDITIONAL_COST_LIST'],
                ['ADDITIONAL_COST'],
                ['CHILD_SETTLEMENT_BALANCE'],
            ],
        });

    const { mutate: toggleAdditionalCostPaid, isLoading: isToggleAdditionalCostPaidLoading } =
        useAppMutation(StaffHTTPService.settlements.toggleAdditionalCostPaid, {
            key: ['TOGGLE_ADDITIONAL_COST_PAID'],
            onSuccess: () =>
                displaySuccess(settlementTexts.children.details.changePaidStatusSuccess),
            onError: () => displayError(commonTexts.errorMessages.actionExecution),
            invalidateQueryKeys: [
                ['ADDITIONAL_COST_DETAILS'],
                ['ADDITIONAL_COST_LIST'],
                ['ADDITIONAL_COST'],
                ['CHILD_SETTLEMENT_BALANCE'],
            ],
        });

    const handleDelete = () => {
        deleteAdditionalCost({ additionalCostId: detailsDto.id });
    };

    const handlePaidStatusChange = (event: { target: { checked: boolean } }) => {
        if (detailsDto.verified) {
            return;
        }

        toggleAdditionalCostPaid({
            additionalCostId: detailsDto.id,
            paid: event.target.checked,
        });
    };

    const useMediaQuery = (query: string) => {
        const [matches, setMatches] = useState(window.matchMedia(query).matches);

        useEffect(() => {
            const media = window.matchMedia(query);
            const listener = () => setMatches(media.matches);
            media.addEventListener('change', listener);
            return () => media.removeEventListener('change', listener);
        }, [query]);

        return matches;
    };

    const isMobile = useMediaQuery('(max-width: 992px)');

    return (
        <div>
            <Header>
                <AppFlex align="center" gap="small" wrap="wrap">
                    <AppTitle level={4} marginTop={16} marginBottom={16}>
                        Dodatkowy koszt nr {index + 1}
                    </AppTitle>
                    <Checkbox
                        checked={detailsDto.paid}
                        disabled={detailsDto.verified || isToggleAdditionalCostPaidLoading}
                        onChange={handlePaidStatusChange}
                    >
                        <AppFlex align="center" gap="small">
                            <span>
                                {settlementTexts.children.manualBill.settlementDetails.paid}
                            </span>
                            <AppButton
                                type="text"
                                size="small"
                                margin={0}
                                accessibilityLabel={
                                    settlementTexts.children.manualBill.settlementDetails
                                        .paidTooltip
                                }
                                icon={
                                    <AppTooltipQuestionMarkIcon
                                        tooltip={
                                            settlementTexts.children.manualBill.settlementDetails
                                                .paidTooltip
                                        }
                                        color={colors.tooltip.tooltip3}
                                        size={16}
                                    />
                                }
                                onMouseDown={(event) => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                }}
                                onClick={(event) => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                }}
                            />
                        </AppFlex>
                    </Checkbox>
                </AppFlex>
            </Header>

            <div
                style={{
                    display: 'flex',
                    flexDirection: isMobile ? 'column' : 'row',
                    gap: 16,
                    alignItems: 'flex-start',
                    width: '100%',
                }}
            >
                <div style={{ flex: 1 }}>
                    <AppCardListItem
                        key={detailsDto.id}
                        index={0}
                        listItem={undefined}
                        options={
                            !detailsDto.verified
                                ? [
                                      {
                                          label: 'Edytuj',
                                          onClick: () => setManualBillEditModalOpen(true),
                                      },
                                      {
                                          label: 'Usuń',
                                          onClick: () => setAdditionalCostDeleteModalOpen(true),
                                          isLoading: deleteAdditionalCostIsLoading,
                                      },
                                  ]
                                : undefined
                        }
                    >
                        <DetailsRow>
                            <DetailsColumn
                                title={settlementTexts.children.manualBill.settlementDetails.name}
                                span={6}
                            >
                                {detailsDto.name}
                            </DetailsColumn>

                            <DetailsColumn
                                title={
                                    settlementTexts.children.manualBill.settlementDetails.issueDate
                                }
                                span={6}
                            >
                                {detailsDto.issueDate}
                            </DetailsColumn>

                            <DetailsColumn
                                title={
                                    settlementTexts.children.manualBill.settlementDetails.dueDate
                                }
                                span={6}
                            >
                                {detailsDto.dueDate}
                            </DetailsColumn>
                            <DetailsColumn
                                title={
                                    settlementTexts.children.manualBill.settlementDetails.saleDate
                                }
                                span={6}
                            >
                                {detailsDto.saleDate}
                            </DetailsColumn>
                        </DetailsRow>

                        <DetailsRow>
                            <DetailsColumn
                                title={
                                    settlementTexts.children.manualBill.settlementDetails.nettAmount
                                }
                                span={6}
                            >
                                {detailsDto.nettAmount ? `${detailsDto.nettAmount} zł` : '-'}
                            </DetailsColumn>

                            <DetailsColumn
                                title={
                                    settlementTexts.children.manualBill.settlementDetails.vatRate
                                }
                                span={6}
                            >
                                {settlementVatRateTypeLabels[detailsDto.vatRateType]}
                            </DetailsColumn>

                            <DetailsColumn
                                title={
                                    settlementTexts.children.manualBill.settlementDetails.vatAmount
                                }
                                span={6}
                            >
                                {detailsDto.vatAmount ? `${detailsDto.vatAmount} zł` : '-'}
                            </DetailsColumn>

                            <DetailsColumn
                                title={
                                    settlementTexts.children.manualBill.settlementDetails
                                        .grossAmount
                                }
                                span={6}
                            >
                                {detailsDto.grossAmount ? `${detailsDto.grossAmount} zł` : '-'}
                            </DetailsColumn>
                        </DetailsRow>

                        <DetailsRow>
                            <DetailsColumn
                                title={
                                    settlementTexts.children.manualBill.settlementDetails
                                        .vatExemptionReason
                                }
                                span={6}
                            >
                                {settlementVatReleaseReasonLabels[detailsDto.exemptionReason]}
                            </DetailsColumn>

                            <DetailsColumn
                                title={
                                    settlementTexts.children.manualBill.settlementDetails
                                        .customVatReleaseReason
                                }
                                span={6}
                            >
                                {detailsDto.customExemptionReason}
                            </DetailsColumn>

                            <DetailsColumn
                                title={settlementTexts.children.manualBill.settlementDetails.note}
                                span={24}
                            >
                                {detailsDto.notes}
                            </DetailsColumn>
                        </DetailsRow>
                    </AppCardListItem>
                </div>

                {detailsDto.invoiceData && (
                    <div
                        style={{
                            width: isMobile ? '100%' : 320,
                            flexShrink: 0,
                        }}
                    >
                        <AdditionalCostInvoice invoiceData={detailsDto.invoiceData} />
                    </div>
                )}
            </div>

            <AddEditAdditionalCostModal
                isOpen={manualBillEditModalOpen}
                mode="edit"
                onCancel={() => setManualBillEditModalOpen(false)}
                additionalCostId={detailsDto.id}
            />

            <RemoveModal
                open={additionalCostDeleteModalOpen}
                title="Czy na pewno chcesz usunąć dodatkowy koszt?"
                onOk={handleDelete}
                onCancel={() => setAdditionalCostDeleteModalOpen(false)}
                confirmActionLoading={deleteAdditionalCostIsLoading}
            />
        </div>
    );
};
